import {
  AuditOutlined, EditOutlined, EyeOutlined, PaperClipOutlined,
  PlusOutlined, PrinterOutlined, SearchOutlined, StopOutlined, ApartmentOutlined,
} from "@ant-design/icons";
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Form,
  Input, InputNumber, message, Popconfirm, Row, Select, Space, Spin,
  Table, Tag, Timeline, Tooltip, Typography,
} from "antd";
import { StaticDrawer } from "../../shared/components/StaticDrawer";
import { StaticModal } from "../../shared/components/StaticModal";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePageKeyboardShortcuts } from "../../shared/hooks/usePageKeyboardShortcuts";
import { transportApi } from "../../api/transportApi";
import type { BiltyNumberSeriesIssueDto } from "../../api/transportApi";
import { getDefaultContext } from "../../api/httpClient";
import type { ConsignmentDto } from "../../types/api";
import { getEnteredByUser } from "../../utils/currentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { LookupSelect } from "../../shared/components/LookupSelect";
import { LocationAutoComplete } from "../../shared/components/LocationAutoComplete";
import { SaveActionButton } from "../../shared/components/SaveActionButton";
import { EntityAttachmentsPanel } from "../../shared/components/EntityAttachmentsPanel";
import { printConsignmentStationery } from "../../shared/printing/transportPrintTemplates";
import { useOperationsWorkspace } from "../../shared/components/OperationsTransactionTabs";

type EWayBillRecord = {
  id: number; consignmentId: number; eWayBillNo: string;
  generatedOn: string; validUpto: string; generatedBy: string;
  isCancelled: boolean; cancelledOn?: string; cancelReason?: string;
};

type LookupItem = { id: number; label: string; subtitle?: string };
type PartyField = "partyId" | "consignorId" | "consigneeId";

const PAYMENT_TYPES = [
  { value: "ToPay",      label: "1 – To Pay" },
  { value: "Paid",       label: "2 – Paid" },
  { value: "ToBeBilled", label: "3 – To Be Billed" },
  { value: "FOC",        label: "4 – FOC (Free of Charge)" },
];

const BOOKING_TYPES = [
  { value: "R", label: "R – Retail" },
  { value: "F", label: "F – FTL (Full Truck Load)" },
];

const CALC_ON_OPTIONS = [
  { value: "W", label: "W – Weight" },
  { value: "F", label: "F – Fixed" },
  { value: "P", label: "P – Packages" },
];

const DELIVERY_AT_OPTIONS = [
  { value: "G", label: "G – Godown / Depot" },
  { value: "D", label: "D – Door Delivery" },
];

type FieldOpts = { opts: LookupItem[]; loading: boolean };
const emptyOpts = (): FieldOpts => ({ opts: [], loading: false });

const isCancelled = (row: ConsignmentDto) =>
  (row.status ?? "").toLowerCase().includes("cancel");

const isEditable = (row: ConsignmentDto) => {
  const s = (row.status ?? "").toLowerCase();
  // Only allow editing when still Booked — once dispatched/delivered the document is locked
  return s === "" || s === "booked";
};

const STATUS_COLOR: Record<string, string> = {
  booked: "blue",
  dispatched: "geekblue",
  intransit: "cyan",
  delivered: "green",
  cancelled: "default",
};

export const ConsignmentListPage = () => {
  const workspace = useOperationsWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ConsignmentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const searchInputRef = useRef<any>(null);
  const newBtnRef = useRef<HTMLButtonElement>(null);

  // Create / Edit drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [manualBiltyNumberEnabled, setManualBiltyNumberEnabled] = useState(true); // Manual Bilty is the system default
  const [availableBiltyRanges, setAvailableBiltyRanges] = useState<BiltyNumberSeriesIssueDto[]>([]);
  const [availableBiltyRangesLoading, setAvailableBiltyRangesLoading] = useState(false);
  const [drawerTempKey, setDrawerTempKey] = useState<string | null>(null);
  const [consignorGstin, setConsignorGstin] = useState<string | null>(null);
  const [consigneeGstin, setConsigneeGstin] = useState<string | null>(null);
  const bookingBranchId = Form.useWatch("bookingBranchId", form);

  // View Details drawer
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState<ConsignmentDto | null>(null);

  // Timeline
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState<any[]>([]);

  // Party lookup helpers (inline select + browse modal + quick-add)
  const [fieldOpts, setFieldOpts] = useState<Record<PartyField, FieldOpts>>({
    partyId: emptyOpts(), consignorId: emptyOpts(), consigneeId: emptyOpts(),
  });
  const [partySearchTexts, setPartySearchTexts] = useState<Record<PartyField, string>>({
    partyId: "", consignorId: "", consigneeId: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState<PartyField>("partyId");
  const [modalRows, setModalRows] = useState<LookupItem[]>([]);
  const [modalSearch, setModalSearch] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalPage, setModalPage] = useState(1);
  const [partyCreateForm] = Form.useForm();
  const [partyCreateOpen, setPartyCreateOpen] = useState(false);
  const [partyCreateTarget, setPartyCreateTarget] = useState<PartyField>("partyId");
  const [partyCreateSaving, setPartyCreateSaving] = useState(false);
  const [gstFetching, setGstFetching] = useState(false);

  // Branch inline create
  type BranchField = "bookingBranchId" | "deliveryBranchId";
  const [branchCreateForm] = Form.useForm();
  const [branchCreateOpen, setBranchCreateOpen] = useState(false);
  const [branchCreateTarget, setBranchCreateTarget] = useState<BranchField>("bookingBranchId");
  const [branchCreateSaving, setBranchCreateSaving] = useState(false);
  const [branchCreatePrefill, setBranchCreatePrefill] = useState("");
  const [branchSeeds, setBranchSeeds] = useState<LookupItem[]>([]);

  // Documents drawer
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsConsignment, setDocsConsignment] = useState<ConsignmentDto | null>(null);

  // E-Way Bill
  const [ewbDrawerOpen, setEwbDrawerOpen] = useState(false);
  const [ewbConsignment, setEwbConsignment] = useState<ConsignmentDto | null>(null);
  const [ewbRecord, setEwbRecord] = useState<EWayBillRecord | null>(null);
  const [ewbLoading, setEwbLoading] = useState(false);
  const [ewbSaving, setEwbSaving] = useState(false);
  const [ewbForm] = Form.useForm();
  const [ewbCancelForm] = Form.useForm();
  const [ewbCancelOpen, setEwbCancelOpen] = useState(false);
  const [ewbCancelSaving, setEwbCancelSaving] = useState(false);

  // All state is declared — safe to derive composite flags
  const anyOverlayOpen = drawerOpen || viewOpen || timelineOpen || ewbDrawerOpen || modalOpen || partyCreateOpen || branchCreateOpen || docsOpen;

  // Auto-focus first input when create drawer opens
  useEffect(() => {
    if (!drawerOpen) return;
    const timer = setTimeout(() => {
      const drawer = document.querySelector(".static-overlay-drawer .ant-drawer-body");
      const first = drawer?.querySelector<HTMLElement>("input, .ant-select-selector");
      first?.focus();
    }, 120);
    return () => clearTimeout(timer);
  }, [drawerOpen]);

  // Page-level keyboard shortcuts (disabled when any overlay is open)
  usePageKeyboardShortcuts({
    "alt+n": () => void openCreateDrawer(),
    "f5":    () => void load(),
    "f3":    () => searchInputRef.current?.focus(),
  }, !anyOverlayOpen);

  // Create/edit drawer shortcuts (disabled when any sub-modal is open)
  usePageKeyboardShortcuts({
    "ctrl+s": () => void onSave(),
    "alt+w":  () => {
      if (!editingId && drawerTempKey) void transportApi.deleteAttachmentsByTempKey(drawerTempKey);
      setDrawerTempKey(null);
      setDrawerOpen(false);
      form.resetFields();
    },
  }, drawerOpen && !partyCreateOpen && !branchCreateOpen && !modalOpen);

  // Party create modal shortcuts
  usePageKeyboardShortcuts({
    "ctrl+s": () => void createPartyInline(),
    "alt+w":  () => { setPartyCreateOpen(false); partyCreateForm.resetFields(); },
  }, partyCreateOpen);

  // Branch create modal shortcuts
  usePageKeyboardShortcuts({
    "ctrl+s": () => void createBranchInline(),
    "alt+w":  () => { setBranchCreateOpen(false); branchCreateForm.resetFields(); },
  }, branchCreateOpen);

  const branchFetcher = useCallback(
    (q: string, page: number, pageSize: number) => transportApi.searchBranchLookups(q, page, pageSize),
    []
  );

  // ── Load ─────────────────────────────────────────────────────────────

  const load = async () => {
    setLoading(true);
    try {
      const result = await transportApi.listConsignments({ page: 1, pageSize: 500, q: search.trim() || undefined });
      setRows(result.data);
      setPage(1);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load consignments"));
    } finally {
      setLoading(false);
    }
  };

  const loadNumberingSettings = useCallback(async () => {
    try {
      const settings = await transportApi.getAccountingSettings();
      setManualBiltyNumberEnabled(!!settings.isManualBiltyNumberEnabled);
      return !!settings.isManualBiltyNumberEnabled;
    } catch {
      setManualBiltyNumberEnabled(true); // keep Manual Bilty default on API error
      return true;
    }
  }, []);

  useEffect(() => { if (workspace?.mode === "data") void load(); }, [workspace?.mode]);

  useEffect(() => {
    void loadNumberingSettings();
  }, [loadNumberingSettings]);

  useEffect(() => {
    // Bilty/LR is owned by the booking branch. Never retain a value or range
    // loaded for the previously selected branch.
    if (drawerOpen && !editingId) {
      form.setFieldValue("consignmentNo", undefined);
      setAvailableBiltyRanges([]);
    }

    if (!drawerOpen || editingId || !manualBiltyNumberEnabled || !bookingBranchId) {
      setAvailableBiltyRanges([]);
      setAvailableBiltyRangesLoading(false);
      return;
    }

    let cancelled = false;
    setAvailableBiltyRangesLoading(true);
    Promise.all([
      transportApi.listAvailableBiltyNumberSeries(Number(bookingBranchId)),
      transportApi.getNextDocumentNumber("Consignment", Number(bookingBranchId)),
    ])
      .then(([ranges, nextResult]) => {
        if (cancelled) return;
        setAvailableBiltyRanges(ranges);
        const nextNumber = nextResult.nextNumber;
        const belongsToSelectedBranchRange = nextNumber != null && ranges.some((range) =>
          range.assignedBranchId === Number(bookingBranchId) && range.isActive &&
          range.startNumber <= nextNumber && nextNumber <= range.endNumber
        );
        form.setFieldValue("consignmentNo", belongsToSelectedBranchRange ? String(nextNumber) : undefined);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableBiltyRanges([]);
        form.setFieldValue("consignmentNo", undefined);
      })
      .finally(() => { if (!cancelled) setAvailableBiltyRangesLoading(false); });

    return () => { cancelled = true; };
  }, [bookingBranchId, drawerOpen, editingId, manualBiltyNumberEnabled]);

  // Deep-link: ?consignmentId=X opens the record; openEwb=1 opens its e-way bill workflow.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const consignmentId = Number(params.get("consignmentId"));
    const openEwb = params.get("openEwb") === "1";
    if (!consignmentId || rows.length === 0 || loading) return;
    const row = rows.find((r) => r.id === consignmentId);
    if (!row) return;
    if (openEwb) void openEwbDrawer(row);
    else void openEditDrawer(row);
    navigate("/consignments", { replace: true });
  }, [location.search, navigate, rows, loading]);

  // ── Party lookup helpers ──────────────────────────────────────────────

  const setFieldLoading = (field: PartyField, val: boolean) =>
    setFieldOpts(prev => ({ ...prev, [field]: { ...prev[field], loading: val } }));

  const setFieldOptsData = (field: PartyField, items: LookupItem[]) =>
    setFieldOpts(prev => ({ ...prev, [field]: { ...prev[field], opts: items, loading: false } }));

  const mergeOption = (field: PartyField, item: LookupItem) =>
    setFieldOpts(prev => {
      const merged = [...prev[field].opts.filter(x => x.id !== item.id), item];
      return { ...prev, [field]: { ...prev[field], opts: merged } };
    });

  const searchParties = async (field: PartyField, q: string) => {
    setPartySearchTexts(prev => ({ ...prev, [field]: q }));
    setFieldLoading(field, true);
    try {
      const data = await transportApi.searchPartyLookups(q, 1, 20);
      setFieldOptsData(field, data.items ?? []);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load parties"));
      setFieldLoading(field, false);
    }
  };

  const loadModalLookups = async (q = modalSearch, p = modalPage) => {
    setModalLoading(true);
    try {
      const result = await transportApi.searchPartyLookups(q, p, 20);
      setModalRows(result.items ?? []);
      setModalTotal(result.total ?? 0);
      setModalPage(p);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load parties"));
    } finally {
      setModalLoading(false);
    }
  };

  const openModal = (target: PartyField) => {
    setModalTarget(target);
    setModalSearch("");
    setModalOpen(true);
    void loadModalLookups("", 1);
  };

  const selectFromModal = (row: LookupItem) => {
    form.setFieldValue(modalTarget, row.id);
    mergeOption(modalTarget, row);
    setModalOpen(false);
  };

  const createPartyInline = async () => {
    const values = await partyCreateForm.validateFields();
    setPartyCreateSaving(true);
    try {
      const created = await transportApi.createPartyLookup({
        name: values.name, code: values.code, phone: values.phone, type: values.type,
      });
      form.setFieldValue(partyCreateTarget, created.id);
      mergeOption(partyCreateTarget, created);
      setPartyCreateOpen(false);
      partyCreateForm.resetFields();
      message.success("Party created and selected");
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to create party"));
    } finally {
      setPartyCreateSaving(false);
    }
  };

  const fetchGstDetails = async () => {
    const gstin: string = (partyCreateForm.getFieldValue("gstin") ?? "").trim().toUpperCase();
    if (gstin.length !== 15) {
      message.warning("GSTIN must be exactly 15 characters");
      return;
    }
    setGstFetching(true);
    try {
      // Derive legal name from GSTIN structure (PAN is chars 3–12).
      // Replace this call with your GST verification API integration.
      // e.g. await transportApi.verifyGstin(gstin) → { legalName, tradeName, address, ... }
      // For now we pre-fill what we can from the GSTIN itself.
      const stateCode = gstin.substring(0, 2);
      const pan       = gstin.substring(2, 12);
      partyCreateForm.setFieldsValue({
        code: pan,
        // name and phone must still be filled by the user until a real API is wired
      });
      message.info(`GSTIN accepted (State: ${stateCode}, PAN: ${pan}). Wire a GST verification API to auto-fill the party name and address.`);
    } catch {
      message.error("Unable to fetch GSTIN details");
    } finally {
      setGstFetching(false);
    }
  };

  const createBranchInline = async () => {
    const values = await branchCreateForm.validateFields();
    setBranchCreateSaving(true);
    try {
      const created = await transportApi.createBranchLookup({
        branchName: values.branchName,
        branchCode: values.branchCode,
        city: values.city,
      });
      // Seed into both branch LookupSelects immediately
      setBranchSeeds(prev => [created, ...prev.filter(b => b.id !== created.id)]);
      form.setFieldValue(branchCreateTarget, created.id);
      setBranchCreateOpen(false);
      branchCreateForm.resetFields();
      message.success("Branch created and selected");
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to create branch"));
    } finally {
      setBranchCreateSaving(false);
    }
  };

  const toSelectOpts = (items: LookupItem[]) =>
    items.map(x => ({ value: x.id, label: x.subtitle ? `${x.label} (${x.subtitle})` : x.label }));

  const openPartyCreate = (field: PartyField, prefillName?: string) => {
    partyCreateForm.resetFields();
    if (prefillName) partyCreateForm.setFieldValue("name", prefillName);
    setPartyCreateTarget(field);
    setPartyCreateOpen(true);
  };

  const renderPartySelect = (field: PartyField, placeholder: string) => {
    const state = fieldOpts[field];
    const searchText = partySearchTexts[field] ?? "";
    const hasResults = state.opts.length > 0;

    const notFoundContent = state.loading ? (
      <div style={{ padding: "8px 12px", textAlign: "center" }}><Spin size="small" /></div>
    ) : searchText.trim() && !hasResults ? (
      <div style={{ padding: "4px 8px" }}>
        <Button
          type="link" size="small" icon={<PlusOutlined />}
          style={{ color: "var(--primary, #1677ff)", padding: 0, height: "auto" }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => openPartyCreate(field, searchText.trim())}
        >
          Add &ldquo;{searchText.trim()}&rdquo;
        </Button>
        <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, paddingLeft: 2 }}>
          or press <kbd style={{ padding: "0 4px", border: "1px solid currentColor", borderRadius: 3, fontSize: 10 }}>Enter</kbd>
        </div>
      </div>
    ) : searchText.trim() ? (
      <div style={{ padding: "6px 12px", opacity: 0.6, fontSize: 12 }}>No results found</div>
    ) : undefined;

    return (
      <Select
        showSearch allowClear placeholder={placeholder}
        loading={state.loading} filterOption={false}
        options={toSelectOpts(state.opts) as any}
        onSearch={(v) => void searchParties(field, v)}
        onDropdownVisibleChange={(vis) => {
          if (vis && state.opts.length === 0) void searchParties(field, "");
          if (!vis) setPartySearchTexts(prev => ({ ...prev, [field]: "" }));
        }}
        onKeyDown={(e) => {
          if (
            e.key === "Enter"
            && searchText.trim()
            && !hasResults
            && !state.loading
          ) {
            e.preventDefault();
            e.stopPropagation();
            openPartyCreate(field, searchText.trim());
          }
        }}
        notFoundContent={notFoundContent}
        popupRender={(menu) => (
          <>
            {menu}
            <div style={{ padding: "6px 8px", display: "flex", gap: 6, borderTop: "1px solid var(--app-bg)" }}>
              <Button size="small" icon={<SearchOutlined />} onMouseDown={(e) => e.preventDefault()} onClick={() => openModal(field)}>Browse</Button>
              <Button
                size="small" icon={<PlusOutlined />}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openPartyCreate(field, searchText.trim())}
              >
                Add New
              </Button>
            </div>
          </>
        )}
      />
    );
  };

  // ── Create / Edit handlers ────────────────────────────────────────────

  const openCreateDrawer = async () => {
    await loadNumberingSettings();
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ paymentType: "ToPay", bookingDate: dayjs(), totalPackages: 1, freightAmount: 0, bookingType: "R", calculateOn: "W", deliveryAt: "G", consignmentNo: "" });
    setAvailableBiltyRanges([]);
    setBranchSeeds([]);
    setFieldOpts({ partyId: emptyOpts(), consignorId: emptyOpts(), consigneeId: emptyOpts() });
    setDrawerTempKey(crypto.randomUUID());
    setDrawerOpen(true);
  };

  useEffect(() => { if (workspace?.mode === "entry") void openCreateDrawer(); }, [workspace?.mode]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("new") !== "1") return;
    const bookingBranchId = Number(params.get("bookingBranchId"));
    const consignmentNo = params.get("consignmentNo") ?? "";
    void (async () => {
      await openCreateDrawer();
      if (bookingBranchId > 0) {
        try {
          const result = await transportApi.searchBranchLookups("", 1, 200);
          const branch = result.items.find((item) => item.id === bookingBranchId);
          if (branch) setBranchSeeds([branch]);
        } catch { /* the numeric value remains valid even if the label lookup fails */ }
      }
      form.setFieldsValue({ bookingBranchId: bookingBranchId || undefined, consignmentNo });
      navigate("/consignments", { replace: true });
    })();
  }, [location.search, navigate]);

  const openEditDrawer = async (row: ConsignmentDto) => {
    setEditingId(row.id);
    setDrawerOpen(true);
    try {
      // Fetch full record (list may be missing charges/items)
      const full = await transportApi.getConsignment(row.id);
      const freight = full.charges?.find(c => c.chargeType === "freight")?.amount ?? 0;

      // Pre-seed lookups so the Select shows the current label
      const primeParty = async (field: PartyField, id: number) => {
        try {
          const res = await transportApi.searchPartyLookups("", 1, 20);
          const found = res.items?.find(x => x.id === id);
          if (found) mergeOption(field, found);
        } catch { /* ignore */ }
      };
      await Promise.all([
        primeParty("partyId", full.partyId),
        primeParty("consignorId", full.consignorId),
        primeParty("consigneeId", full.consigneeId),
      ]);

      form.setFieldsValue({
        bookingDate:       dayjs(full.bookingDate),
        partyId:           full.partyId,
        consignorId:       full.consignorId,
        consigneeId:       full.consigneeId,
        bookingBranchId:   full.bookingBranchId,
        deliveryBranchId:  full.deliveryBranchId,
        origin:            full.origin,
        destination:       full.destination,
        totalWeight:       full.totalWeight,
        chargedWeight:     full.chargedWeight,
        totalPackages:     full.totalPackages,
        declaredValue:     full.declaredValue,
        goodsName:         full.goodsName ?? "",
        goodsCode:         full.goodsCode ?? "",
        privateMark:       full.privateMark ?? "",
        paymentType:       full.paymentType,
        bookingType:       full.bookingType,
        deliveryAt:        full.deliveryAt,
        freightAmount:     freight,
        rate:              full.rate,
        calculateOn:       full.calculateOn,
        invoiceNo:         full.invoiceNo ?? "",
        eWayBillNo:        full.eWayBillNo ?? "",
        remarks:           full.remarks ?? "",
      });
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to load consignment details"));
      setDrawerOpen(false);
    }
  };

  const onSave = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (error: any) {
      const firstError = error?.errorFields?.[0];
      if (firstError?.name) {
        form.scrollToField(firstError.name, { block: "center" });
        requestAnimationFrame(() => form.getFieldInstance(firstError.name)?.focus?.());
      }
      message.warning(firstError?.errors?.[0] ?? "Complete the required consignment details.");
      return;
    }
    const ctx = getDefaultContext();
    const enteredBy = getEnteredByUser();
    setSaving(true);
    try {
      if (editingId !== null) {
        // ── Update ────────────────────────────────────────────────────
        await transportApi.updateConsignment(editingId, {
          ...ctx,
          enteredBy,
          bookingDate:     values.bookingDate.toISOString(),
          consignorId:     values.consignorId,
          consigneeId:     values.consigneeId,
          bookingBranchId: values.bookingBranchId,
          deliveryBranchId: values.deliveryBranchId,
          totalWeight:     values.totalWeight,
          chargedWeight:   values.chargedWeight ?? values.totalWeight ?? 0,
          totalPackages:   values.totalPackages,
          declaredValue:   values.declaredValue ?? 0,
          goodsCode:       values.goodsCode ?? null,
          goodsName:       values.goodsName ?? null,
          privateMark:     values.privateMark ?? null,
          rate:            values.rate ?? 0,
          calculateOn:     values.calculateOn ?? "W",
          bookingType:     values.bookingType ?? "R",
          deliveryAt:      values.deliveryAt ?? "G",
          invoiceNo:       values.invoiceNo ?? null,
          eWayBillNo:      values.eWayBillNo ?? null,
          paymentType:     values.paymentType,
          remarks:         values.remarks ?? null,
        });
        message.success("Consignment updated");
      } else {
        // ── Create ────────────────────────────────────────────────────
        const created = await transportApi.createConsignment({
          ...ctx,
          enteredBy,
          consignmentNo: manualBiltyNumberEnabled ? values.consignmentNo?.trim() : undefined,
          bookingDate:     values.bookingDate.toISOString(),
          consignorId:     values.consignorId,
          consigneeId:     values.consigneeId,
          bookingBranchId: values.bookingBranchId,
          deliveryBranchId: values.deliveryBranchId,
          totalWeight:     values.totalWeight,
          chargedWeight:   values.chargedWeight ?? values.totalWeight ?? 0,
          totalPackages:   values.totalPackages,
          declaredValue:   values.declaredValue ?? 0,
          goodsCode:       values.goodsCode ?? null,
          goodsName:       values.goodsName ?? null,
          privateMark:     values.privateMark ?? null,
          rate:            values.rate ?? 0,
          calculateOn:     values.calculateOn ?? "W",
          bookingType:     values.bookingType ?? "R",
          deliveryAt:      values.deliveryAt ?? "G",
          ccAttached:      false,
          invoiceNo:       values.invoiceNo ?? null,
          eWayBillNo:      values.eWayBillNo ?? null,
          paymentType:     values.paymentType,
          remarks:         values.remarks ?? null,
          charges: [{
            ...ctx, enteredBy,
            chargeType: "freight",
            amount: values.freightAmount ?? 0,
            isTaxable: true,
          }],
          items: [],
          tempKey: drawerTempKey ?? undefined,
        });
        // Clear the tempKey — files are now bound server-side; cancel must not delete them.
        setDrawerTempKey(null);
        // Stay in the drawer in edit-mode so user can immediately attach more documents
        setEditingId(created.id);
        message.success(`Consignment ${created.consignmentNo ?? `#${created.id}`} created — you can now attach documents below`);
        await load();
        return; // keep drawer open
      }
      setDrawerOpen(false);
      form.resetFields();
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, editingId ? "Unable to update consignment" : "Unable to create consignment"));
    } finally {
      setSaving(false);
    }
  };

  const onCancel = async (row: ConsignmentDto) => {
    try {
      await transportApi.cancelConsignment(row.id, getEnteredByUser());
      message.success("Consignment cancelled");
      await load();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to cancel consignment"));
    }
  };

  const onPrintConsignment = async (row: ConsignmentDto) => {
    try {
      const report = await transportApi.getConsignmentPrintReport(row.id);
      printConsignmentStationery(report);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to print consignment"));
    }
  };

  const onTimeline = async (row: ConsignmentDto) => {
    try {
      const data = await transportApi.getTrackingTimeline(row.organizationId, row.financialYearId, row.id);
      setTimeline(data ?? []);
      setTimelineOpen(true);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load tracking timeline"));
    }
  };

  // ── E-Way Bill ────────────────────────────────────────────────────────

  const openEwbDrawer = async (row: ConsignmentDto) => {
    setEwbConsignment(row);
    setEwbRecord(null);
    setEwbDrawerOpen(true);
    setEwbLoading(true);
    try {
      const data = await transportApi.getEWayBillByConsignment(row.id);
      setEwbRecord(data as EWayBillRecord);
    } catch (e: any) {
      if (e?.response?.status !== 404) {
        message.error(getApiErrorMessage(e, "Unable to load E-Way Bill"));
      }
    } finally {
      setEwbLoading(false);
    }
  };

  const recordEwb = async () => {
    const values = await ewbForm.validateFields();
    const ctx = getDefaultContext();
    setEwbSaving(true);
    try {
      const created = await transportApi.generateEWayBill({
        organizationId:  ctx.organizationId,
        financialYearId: ctx.financialYearId,
        consignmentId:   ewbConsignment!.id,
        eWayBillNo:      values.eWayBillNo,
        validUpto:       values.validUpto.toISOString(),
        generatedBy:     getEnteredByUser(),
      });
      setEwbRecord(created as EWayBillRecord);
      ewbForm.resetFields();
      message.success("E-Way Bill recorded and tracking activated");
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to record E-Way Bill"));
    } finally {
      setEwbSaving(false);
    }
  };

  const cancelEwb = async () => {
    const values = await ewbCancelForm.validateFields();
    const ctx = getDefaultContext();
    setEwbCancelSaving(true);
    try {
      await transportApi.cancelEWayBill(ewbRecord!.id, {
        organizationId: ctx.organizationId,
        financialYearId: ctx.financialYearId,
        reason: values.reason,
      });
      setEwbRecord(prev => prev
        ? { ...prev, isCancelled: true, cancelReason: values.reason, cancelledOn: new Date().toISOString() }
        : null);
      setEwbCancelOpen(false);
      ewbCancelForm.resetFields();
      message.success("E-Way Bill cancelled");
    } catch (e) {
      message.error(getApiErrorMessage(e, "Unable to cancel E-Way Bill"));
    } finally {
      setEwbCancelSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────

  const filteredRows = rows.filter((row) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [
      row.consignmentNo ?? "", row.origin, row.destination,
      row.status ?? "", String(row.totalPackages), String(row.totalWeight),
    ].some((v) => v.toLowerCase().includes(term));
  });

  const drawerTitle = editingId
    ? `Edit Consignment — ${rows.find(r => r.id === editingId)?.consignmentNo ?? `#${editingId}`}`
    : "New Consignment";

  const modalPartyLabel = modalTarget === "partyId" ? "Billing Party"
    : modalTarget === "consignorId" ? "Consignor (Sender)" : "Consignee (Receiver)";

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Card className="panel-card">
      <div className="panel-title-row operations-data-only">
        <Space align="center" size={8}>
          <Typography.Title level={4} style={{ margin: 0 }}>Consignments</Typography.Title>
          <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>C</kbd></span>
        </Space>
        <Space>
          <span className="kbd-hint"><kbd>Alt</kbd>+<kbd>N</kbd></span>
          <Button
            ref={newBtnRef}
            type="primary"
            icon={<PlusOutlined />}
            className={search.trim() && filteredRows.length === 0 ? "new-btn-highlight" : ""}
            onClick={() => void openCreateDrawer()}
          >
            New Consignment
          </Button>
        </Space>
      </div>

      <Space className="operations-data-only" style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }} wrap>
        <Space.Compact>
          <Input
            ref={searchInputRef}
            className="panel-search-input"
            prefix={<SearchOutlined />}
            placeholder="Search Bilty / LR no, origin, destination, status…  F3"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => void load()}
            style={{ width: 400 }}
          />
          <Button icon={<SearchOutlined />} onClick={() => void load()}>
            Search
          </Button>
        </Space.Compact>
        <Typography.Text type="secondary">
          {search.trim() && filteredRows.length === 0
            ? "No results — press Alt+N to create a new consignment"
            : `Showing ${filteredRows.length} of ${rows.length} loaded consignments`}
        </Typography.Text>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredRows}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: filteredRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} consignments`,
          onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); },
        }}
        columns={[
          { title: "#", width: 50, render: (_, __, i) => ((page - 1) * pageSize) + i + 1 },
          { title: "No", dataIndex: "consignmentNo", width: 130 },
          {
            title: "Date", dataIndex: "bookingDate", width: 90,
            render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—",
          },
          { title: "Origin", dataIndex: "origin" },
          { title: "Destination", dataIndex: "destination" },
          { title: "Pkgs", dataIndex: "totalPackages", width: 55, align: "right" },
          { title: "Wt (kg)", dataIndex: "totalWeight", width: 75, align: "right" },
          {
            title: "Payment", dataIndex: "paymentType", width: 100,
            render: (v: string) => {
              const map: Record<string, string> = { ToPay: "To Pay", Paid: "Paid", ToBeBilled: "Bill", FOC: "FOC" };
              return <Tag>{map[v] ?? v}</Tag>;
            },
          },
          {
            title: "Status", dataIndex: "status", width: 100,
            render: (v: string) => {
              const label = v ?? "Booked";
              const color = STATUS_COLOR[label.toLowerCase()] ?? "blue";
              return <Tag color={color}>{label}</Tag>;
            },
          },
          {
            title: "Actions", width: 295, align: "right",
            render: (_, row) => (
              <Space size={4}>
                <Tooltip placement="bottom" title="View full details">
                  <Button size="small" icon={<EyeOutlined />} onClick={() => { setViewRow(row); setViewOpen(true); }} />
                </Tooltip>
                <Tooltip placement="bottom" title="Print Bilty / LR">
                  <Button size="small" icon={<PrinterOutlined />} onClick={() => void onPrintConsignment(row)} />
                </Tooltip>
                <Tooltip placement="bottom" title={!isEditable(row) ? "Cannot edit — consignment is past Booked status" : "Edit consignment"}>
                  <Button
                    size="small" icon={<EditOutlined />}
                    disabled={!isEditable(row)}
                    onClick={() => void openEditDrawer(row)}
                  />
                </Tooltip>
                <Button size="small" icon={<PaperClipOutlined />}
                  onClick={() => { setDocsConsignment(row); setDocsOpen(true); }}>Docs</Button>
                <Button size="small" icon={<AuditOutlined />} onClick={() => void openEwbDrawer(row)}>E-Way</Button>
                <Button size="small" onClick={() => void onTimeline(row)}>Timeline</Button>
                {isCancelled(row) ? (
                  <Tooltip placement="bottom" title="Already cancelled">
                    <Button size="small" danger disabled>Cancelled</Button>
                  </Tooltip>
                ) : (
                  <Popconfirm
                    title="Cancel this consignment?"
                    description="This cannot be undone. The consignment record will be marked cancelled."
                    onConfirm={() => void onCancel(row)}
                    okText="Yes, Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" danger icon={<StopOutlined />}>Cancel</Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* ── Create / Edit Drawer ────────────────────────────────────── */}
      <StaticDrawer
        title={drawerTitle}
        width="100%"
        open={drawerOpen}
        onClose={() => {
          if (!editingId && drawerTempKey) void transportApi.deleteAttachmentsByTempKey(drawerTempKey);
          setDrawerOpen(false);
          form.resetFields();
        }}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span className="kbd-hint"><kbd>Ctrl</kbd>+<kbd>S</kbd> Save &nbsp;·&nbsp; <kbd>Alt</kbd>+<kbd>W</kbd> Close</span>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => {
                if (!editingId && drawerTempKey) void transportApi.deleteAttachmentsByTempKey(drawerTempKey);
                setDrawerTempKey(null);
                setDrawerOpen(false);
                form.resetFields();
              }}>Cancel</Button>
              <SaveActionButton
                saving={saving}
                idleText={editingId ? "Save Changes" : "Create Consignment"}
                savingText={editingId ? "Saving..." : "Creating..."}
                onClick={() => void onSave()}
              />
            </div>
          </div>
        }
        destroyOnHidden maskClosable={false}      >
        {editingId && (
          <Alert
            type="info" showIcon style={{ marginBottom: 16 }}
            message="Editing a booked consignment"
            description="Freight charges cannot be changed after creation. Route, weight, packages, and reference fields can be updated as long as the consignment has not been dispatched."
          />
        )}

        <Form form={form} layout="vertical">
          {/* Row 1: Booking Station | Delivery Station | Date */}
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="bookingBranchId" label="Booking Station"
                tooltip="Branch where this consignment is being booked."
                rules={[{ required: true, message: "Select booking branch" }]}>
                <LookupSelect
                  fetcher={branchFetcher}
                  modalTitle="Select Booking Station"
                  placeholder="Search branch / station..."
                  seededOptions={branchSeeds}
                  addNewLabel="Branch"
                  onAddNew={(q) => { setBranchCreatePrefill(q); setBranchCreateTarget("bookingBranchId"); setBranchCreateOpen(true); }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="deliveryBranchId" label="Delivery Station"
                tooltip="Branch responsible for last-mile delivery at destination."
                dependencies={["bookingBranchId"]}
                rules={[
                  { required: true, message: "Select delivery branch" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return value && value === getFieldValue("bookingBranchId")
                        ? Promise.reject(new Error("Booking and Delivery Station must be different."))
                        : Promise.resolve();
                    },
                  }),
                ]}>
                <LookupSelect
                  fetcher={branchFetcher}
                  modalTitle="Select Delivery Station"
                  placeholder="Search branch / station..."
                  seededOptions={branchSeeds}
                  addNewLabel="Branch"
                  onAddNew={(q) => { setBranchCreatePrefill(q); setBranchCreateTarget("deliveryBranchId"); setBranchCreateOpen(true); }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bookingDate" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          {!editingId && manualBiltyNumberEnabled && (
            <Form.Item
              name="consignmentNo"
              label="Bilty / LR No"
              tooltip="Select a pre-approved bilty number for this booking branch. Numbers come from the Bilty Stationery ranges issued to this branch."
              rules={[{ required: true, message: "Select a Bilty / LR number" }]}
              extra={availableBiltyRangesLoading
                ? <Space size={6}><Spin size="small" /><Typography.Text type="secondary">Loading assigned Bilty number for this branch…</Typography.Text></Space>
                : undefined}
            >
              <Select
                showSearch
                allowClear
                placeholder={
                  !bookingBranchId
                    ? "Select booking branch first"
                    : availableBiltyRangesLoading
                    ? "Loading available bilty numbers…"
                    : "Select bilty number…"
                }
                disabled={!bookingBranchId}
                loading={availableBiltyRangesLoading}
                filterOption={(input, option) =>
                  String(option?.value ?? "").includes(input.trim())
                }
                optionRender={(opt) => {
                  const numVal = String(opt.value);
                  const range = availableBiltyRanges.find(
                    r => r.isActive && r.availableCount > 0 && String(r.startNumber + r.usedCount) === numVal
                  );
                  return (
                    <div>
                      <span style={{ fontWeight: 600 }}>{numVal}</span>
                      {range && (
                        <span style={{ fontSize: 11, opacity: 0.55, marginLeft: 10 }}>
                          Range {range.startNumber}–{range.endNumber} &nbsp;·&nbsp; {range.availableCount} left &nbsp;·&nbsp; {range.assignedBranchName}
                        </span>
                      )}
                    </div>
                  );
                }}
                options={availableBiltyRanges
                  .filter(r => r.isActive && r.availableCount > 0)
                  .map(r => ({
                    value: String(r.startNumber + r.usedCount),
                    label: String(r.startNumber + r.usedCount),
                  }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && bookingBranchId && !availableBiltyRangesLoading &&
                      availableBiltyRanges.filter(r => r.isActive && r.availableCount > 0).length === 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate("/bilty-stationery");
                  }
                }}
                notFoundContent={
                  !bookingBranchId ? (
                    <div style={{ padding: "6px 12px", opacity: 0.6, fontSize: 12 }}>Select booking branch first</div>
                  ) : availableBiltyRangesLoading ? (
                    <div style={{ padding: "8px 12px", textAlign: "center" }}><Spin size="small" /></div>
                  ) : (
                    <div style={{ padding: "8px 12px" }}>
                      <div style={{ opacity: 0.6, fontSize: 12, marginBottom: 6 }}>No bilty ranges issued to this branch</div>
                      <Button
                        type="link" size="small" icon={<PlusOutlined />}
                        style={{ padding: 0, height: "auto" }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => navigate("/bilty-stationery")}
                      >
                        Manage Bilty Stationery
                      </Button>
                      <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, paddingLeft: 2 }}>
                        or press <kbd style={{ padding: "0 4px", border: "1px solid currentColor", borderRadius: 3, fontSize: 10 }}>Enter</kbd>
                      </div>
                    </div>
                  )
                }
              />
            </Form.Item>
          )}

          {/* Row: Consignor | Consignee */}
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="consignorId" label={
                <span>Consignor (Sender){consignorGstin ? <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 8, fontSize: 12 }}>GSTIN: {consignorGstin}</span> : null}</span>
              }
                tooltip="The party dispatching / sending the goods."
                rules={[
                  { required: true, message: "Select consignor" },
                  { validator: (_, value) => Number(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Select a valid consignor")) }
                ]}>
                {renderPartySelect("consignorId", "Search sender party...")}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="consigneeId" label={
                <span>Consignee (Receiver){consigneeGstin ? <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 8, fontSize: 12 }}>GSTIN: {consigneeGstin}</span> : null}</span>
              }
                tooltip="The party receiving the goods at the destination."
                rules={[
                  { required: true, message: "Select consignee" },
                  { validator: (_, value) => Number(value) > 0 ? Promise.resolve() : Promise.reject(new Error("Select a valid consignee")) }
                ]}>
                {renderPartySelect("consigneeId", "Search receiver party...")}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="totalPackages" label="Packages"
                tooltip="Total number of packages / articles in this consignment."
                rules={[{ required: true, message: "Enter package count" }]}>
                <InputNumber style={{ width: "100%" }} min={1} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="totalWeight" label="Actual Weight (kg)"
                tooltip="Gross weight of all packages."
                rules={[{ required: true, message: "Enter actual weight" }]}>
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="chargedWeight" label="Charged Weight (kg)"
                tooltip="Weight used for freight calculation. Defaults to actual weight if blank.">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="declaredValue" label="Declared Value (₹)"
                tooltip="Shipper-declared value of goods. Required for insurance and E-Way Bill threshold check (≥ ₹50,000).">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="goodsName" label="Goods Name"
                tooltip="Short description of goods (e.g. M PARTS, ELE GOODS). Used on the consignment note.">
                <Input placeholder="e.g. Machine Parts" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="goodsCode" label="Goods Code"
                tooltip="Internal goods category code.">
                <Input placeholder="Code" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="privateMark" label="Pvt. Mark"
                tooltip="Shipper's private identification mark on packages.">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="paymentType" label="Payment Basis"
                tooltip="Who pays the freight: To Pay (consignee pays), Paid (consignor pays), To Be Billed (monthly billing), or FOC (free)."
                rules={[{ required: true }]}>
                <Select options={PAYMENT_TYPES} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bookingType" label="Booking Type"
                tooltip="R = Retail (part load / LCL). F = FTL (full truck load, single party).">
                <Select options={BOOKING_TYPES} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="deliveryAt" label="Delivery At"
                tooltip="G = Godown/Depot (receiver collects). D = Door delivery (we deliver to address).">
                <Select options={DELIVERY_AT_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="freightAmount" label="Freight (₹)"
                tooltip={editingId ? "Freight charges cannot be changed after creation. To modify freight, cancel and re-book." : "Base freight charge for this consignment."}
                rules={[{ required: !editingId, message: "Enter freight amount" }]}>
                <InputNumber style={{ width: "100%" }} min={0} disabled={!!editingId} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="rate" label="Rate"
                tooltip="Rate per unit (per kg / per package / fixed). Used for freight calculation reference.">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="calculateOn" label="Calculate On"
                tooltip="Unit basis for freight rate: W = per kg Weight, F = Fixed amount, P = per Package.">
                <Select options={CALC_ON_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="invoiceNo" label="Invoice No"
                tooltip="Supplier or seller invoice number(s) for the goods.">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="eWayBillNo" label="E-Way Bill No (reference)"
                tooltip="Optional: enter if the E-Way Bill was generated on the NIC portal before booking. For full EWB tracking use the E-Way button after saving.">
                <Input placeholder="e.g. 311234567890" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remarks" label="Remarks"
            tooltip="Internal notes for this consignment. Not printed on documents.">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--app-bg)" }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Documents &amp; Attachments</div>
          <EntityAttachmentsPanel
            entityType="consignment"
            entityId={editingId ?? 0}
            tempKey={editingId ? undefined : drawerTempKey}
            entityLabel={editingId ? (rows.find(r => r.id === editingId)?.consignmentNo) : undefined}
            branchId={editingId ? undefined : (bookingBranchId ? Number(bookingBranchId) : undefined)}
          />
        </div>
      </StaticDrawer>

      {/* ── View Details Drawer ─────────────────────────────────────── */}
      <StaticDrawer
        title={viewRow ? `Consignment Details — ${viewRow.consignmentNo ?? `#${viewRow.id}`}` : "Consignment Details"}
        width={600}
        open={viewOpen}
        onClose={() => { setViewOpen(false); setViewRow(null); }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            {viewRow && isEditable(viewRow) && (
              <Button icon={<EditOutlined />} onClick={() => { setViewOpen(false); void openEditDrawer(viewRow!); }}>
                Edit
              </Button>
            )}
            {viewRow && (
              <Button icon={<PrinterOutlined />} onClick={() => void onPrintConsignment(viewRow)}>
                Print
              </Button>
            )}
            <Button onClick={() => { setViewOpen(false); setViewRow(null); }}>Close</Button>
          </div>
        }
        destroyOnHidden maskClosable={false}      >
        {viewRow && (
          <Space direction="vertical" style={{ width: "100%" }} size={16}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Consignment No" span={2}>
                <Typography.Text strong>{viewRow.consignmentNo}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Booking Date">
                {dayjs(viewRow.bookingDate).format("DD MMM YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[(viewRow.status ?? "booked").toLowerCase()] ?? "blue"}>
                  {viewRow.status ?? "Booked"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Origin">{viewRow.origin}</Descriptions.Item>
              <Descriptions.Item label="Destination">{viewRow.destination}</Descriptions.Item>
              <Descriptions.Item label="Payment Basis">
                {PAYMENT_TYPES.find(p => p.value === viewRow.paymentType)?.label ?? viewRow.paymentType}
              </Descriptions.Item>
              <Descriptions.Item label="Booking Type">
                {BOOKING_TYPES.find(b => b.value === viewRow.bookingType)?.label ?? viewRow.bookingType}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery At">
                {DELIVERY_AT_OPTIONS.find(d => d.value === viewRow.deliveryAt)?.label ?? viewRow.deliveryAt}
              </Descriptions.Item>
              <Descriptions.Item label="Packages">{viewRow.totalPackages}</Descriptions.Item>
              <Descriptions.Item label="Actual Weight">{viewRow.totalWeight} kg</Descriptions.Item>
              <Descriptions.Item label="Charged Weight">{viewRow.chargedWeight} kg</Descriptions.Item>
              <Descriptions.Item label="Declared Value">₹ {viewRow.declaredValue?.toLocaleString("en-IN")}</Descriptions.Item>
              <Descriptions.Item label="Goods Name">{viewRow.goodsName ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Goods Code">{viewRow.goodsCode ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Invoice No">{viewRow.invoiceNo ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="E-Way Bill No">{viewRow.eWayBillNo ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Private Mark">{viewRow.privateMark ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Rate">
                {viewRow.rate} ({CALC_ON_OPTIONS.find(c => c.value === viewRow.calculateOn)?.label ?? viewRow.calculateOn})
              </Descriptions.Item>
              {viewRow.remarks && (
                <Descriptions.Item label="Remarks" span={2}>{viewRow.remarks}</Descriptions.Item>
              )}
              <Descriptions.Item label="Entered By">{viewRow.enteredBy ?? "—"}</Descriptions.Item>
            </Descriptions>

            {(viewRow.charges?.length ?? 0) > 0 && (
              <>
                <Typography.Text strong>Charges</Typography.Text>
                <Table
                  rowKey="id" size="small" pagination={false}
                  dataSource={viewRow.charges}
                  columns={[
                    { title: "Type", dataIndex: "chargeType" },
                    { title: "Amount (₹)", dataIndex: "amount", align: "right", render: (v: number) => v.toLocaleString("en-IN") },
                    { title: "Taxable", dataIndex: "isTaxable", render: (v: boolean) => v ? "Yes" : "No" },
                  ]}
                />
              </>
            )}
          </Space>
        )}
      </StaticDrawer>

      {/* ── Timeline Drawer ─────────────────────────────────────────── */}
      <StaticDrawer
        title="Tracking Timeline" width={500}
        open={timelineOpen} onClose={() => setTimelineOpen(false)}
        maskClosable={false}
      >
        {timeline.length === 0
          ? <Typography.Text type="secondary">No tracking events found for this consignment.</Typography.Text>
          : (
            <Timeline
              items={timeline.map((item: any) => ({
                color: "blue",
                children: (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{item.eventType ?? item.type ?? "Event"}</div>
                    <div style={{ color: "#6b7280", fontSize: 12 }}>{item.message ?? item.description ?? ""}</div>
                  </div>
                ),
              }))}
            />
          )}
      </StaticDrawer>

      {/* ── E-Way Bill Drawer ────────────────────────────────────────── */}
      <StaticDrawer
        title={`E-Way Bill — ${ewbConsignment?.consignmentNo ?? `CN-${ewbConsignment?.id}`}`}
        width={500}
        open={ewbDrawerOpen}
        onClose={() => { setEwbDrawerOpen(false); setEwbConsignment(null); setEwbRecord(null); }}
        destroyOnHidden maskClosable={false}      >
        {ewbLoading ? (
          <Typography.Text type="secondary">Loading E-Way Bill...</Typography.Text>
        ) : ewbRecord ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              type={ewbRecord.isCancelled ? "error" : dayjs(ewbRecord.validUpto).isBefore(dayjs()) ? "warning" : "success"}
              message={ewbRecord.isCancelled ? "Cancelled" : dayjs(ewbRecord.validUpto).isBefore(dayjs()) ? "Expired" : "Active — Auto-extension ON"}
              description={!ewbRecord.isCancelled && !dayjs(ewbRecord.validUpto).isBefore(dayjs())
                ? "This E-Way Bill is being tracked. The system will auto-extend it within the 8-hour window around expiry (configurable). You can also extend manually from the E-Way Bill Dashboard."
                : undefined}
              showIcon
            />
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Bill No">{ewbRecord.eWayBillNo}</Descriptions.Item>
              <Descriptions.Item label="Generated On">{dayjs(ewbRecord.generatedOn).format("DD MMM YYYY, HH:mm")}</Descriptions.Item>
              <Descriptions.Item label="Valid Upto">
                <span style={{ color: dayjs(ewbRecord.validUpto).isBefore(dayjs()) ? "#ef4444" : "inherit" }}>
                  {dayjs(ewbRecord.validUpto).format("DD MMM YYYY, HH:mm")}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Generated By">{ewbRecord.generatedBy}</Descriptions.Item>
              {ewbRecord.isCancelled && (
                <>
                  <Descriptions.Item label="Cancelled On">{dayjs(ewbRecord.cancelledOn).format("DD MMM YYYY")}</Descriptions.Item>
                  <Descriptions.Item label="Cancel Reason">{ewbRecord.cancelReason}</Descriptions.Item>
                </>
              )}
            </Descriptions>
            <Space>
              {!ewbRecord.isCancelled && (
                <Button danger icon={<StopOutlined />} onClick={() => setEwbCancelOpen(true)}>
                  Cancel E-Way Bill
                </Button>
              )}
              <Button onClick={() => navigate("/ewaybills")}>
                Open EWB Dashboard
              </Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <Alert
              type="info" showIcon
              message="How E-Way Bills work in this system"
              description={
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18, fontSize: 13 }}>
                  <li><strong>Generate on NIC portal</strong> — Log in to <a href="https://ewaybillgst.gov.in" target="_blank" rel="noreferrer">ewaybillgst.gov.in</a> (or sandbox) and generate the EWB for this consignment.</li>
                  <li><strong>Record here</strong> — Enter the EWB number and validity below. The system starts tracking it immediately.</li>
                  <li><strong>Auto-extension</strong> — The system checks every 15 min and extends the EWB automatically within the NIC-allowed 8-hour window before/after expiry (requires NIC API credentials in appsettings).</li>
                  <li><strong>Manual extend / GPS extend / vehicle update</strong> — Available on the <strong>E-Way Bill Dashboard</strong> for any active EWB.</li>
                </ul>
              }
            />
            <Typography.Title level={5} style={{ margin: 0 }}>Record E-Way Bill</Typography.Title>
            <Form form={ewbForm} layout="vertical">
              <Form.Item name="eWayBillNo" label="E-Way Bill Number"
                tooltip="12-digit EWB number as issued by the NIC portal."
                rules={[{ required: true, message: "Enter the e-way bill number" }]}>
                <Input placeholder="e.g. 311234567890" maxLength={12} />
              </Form.Item>
              <Form.Item name="validUpto" label="Valid Upto"
                tooltip="Expiry date/time shown on the NIC-generated EWB."
                rules={[{ required: true, message: "Select validity date" }]}>
                <DatePicker showTime style={{ width: "100%" }} disabledDate={(d) => d.isBefore(dayjs(), "day")} />
              </Form.Item>
              <SaveActionButton
                saving={ewbSaving}
                idleText="Record & Start Tracking"
                savingText="Recording..."
                icon={<AuditOutlined />}
                onClick={() => void recordEwb()}
              />
            </Form>
          </Space>
        )}
      </StaticDrawer>

      {/* ── E-Way Bill Cancel Modal ──────────────────────────────────── */}
      <StaticModal
        title="Cancel E-Way Bill"
        open={ewbCancelOpen}
        onCancel={() => { setEwbCancelOpen(false); ewbCancelForm.resetFields(); }}
        onOk={() => void cancelEwb()}
        okText={ewbCancelSaving ? "Cancelling..." : "Confirm Cancel"}
        okButtonProps={{ danger: true, loading: ewbCancelSaving }}
        destroyOnHidden maskClosable={false}      >
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="NIC allows EWB cancellation only within 24 hours of generation."
          description="After 24 hours, cancellation is not accepted by the NIC API. The EWB record will be marked cancelled in this system but may still be active on the NIC portal."
        />
        <Form form={ewbCancelForm} layout="vertical">
          <Form.Item name="reason" label="Cancellation Reason"
            rules={[{ required: true, message: "Provide a reason" }]}>
            <Input.TextArea rows={3} placeholder="e.g. Goods not dispatched, duplicate entry, wrong consignee..." />
          </Form.Item>
        </Form>
      </StaticModal>

      {/* ── Global Party Browse Modal ────────────────────────────────── */}
      <StaticModal
        title={`Select Party — ${modalPartyLabel}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null} width="100%" destroyOnHidden maskClosable={false}
      >
        <Space style={{ marginBottom: 12 }}>
          <Input.Search
            placeholder="Search by name / code / phone" allowClear
            value={modalSearch}
            onChange={(e) => setModalSearch(e.target.value)}
            onSearch={(v) => { setModalSearch(v); void loadModalLookups(v, 1); }}
            style={{ width: 340 }}
          />
          <Button icon={<PlusOutlined />} onClick={() => { setPartyCreateTarget(modalTarget); setPartyCreateOpen(true); }}>
            Add New
          </Button>
        </Space>
        <Table
          rowKey="id" size="small" loading={modalLoading}
          dataSource={modalRows}
          pagination={{ current: modalPage, total: modalTotal, pageSize: 20, showSizeChanger: false, onChange: (p) => void loadModalLookups(modalSearch, p) }}
          columns={[
            { title: "ID", dataIndex: "id", width: 70 },
            { title: "Name", dataIndex: "label" },
            { title: "Details", dataIndex: "subtitle" },
            { title: "", width: 80, render: (_, row) => <Button size="small" type="primary" onClick={() => selectFromModal(row)}>Select</Button> },
          ]}
        />
      </StaticModal>

      {/* ── Quick Add Party Modal ────────────────────────────────────── */}
      <StaticModal
        title="Add New Party"
        open={partyCreateOpen}
        onCancel={() => { setPartyCreateOpen(false); partyCreateForm.resetFields(); }}
        onOk={() => void createPartyInline()}
        okText={partyCreateSaving ? "Saving..." : "Save Party"}
        okButtonProps={{ loading: partyCreateSaving }}
        destroyOnHidden maskClosable={false}      >
        <Alert
          type="info" showIcon style={{ marginBottom: 14 }}
          message="Enter GSTIN first"
          description="Type the 15-character GSTIN and click 'Fetch from GST' to auto-fill party details. Or fill manually and save directly."
        />
        <Form form={partyCreateForm} layout="vertical">
          {/* GST field – shown first so auto-fill can populate the rest */}
          <Row gutter={8} align="bottom">
            <Col flex="1">
              <Form.Item
                name="gstin"
                label="GSTIN (GST Number)"
                tooltip="15-character GST Identification Number. Auto-fills name and code when fetched."
                rules={[
                  { len: 15, message: "GSTIN must be 15 characters" },
                  { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, message: "Invalid GSTIN format" },
                ]}
              >
                <Input
                  placeholder="e.g. 27AABCU9603R1ZX"
                  maxLength={15}
                  style={{ textTransform: "uppercase" }}
                  onChange={(e) => partyCreateForm.setFieldValue("gstin", e.target.value.toUpperCase())}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label=" ">
                <Button
                  icon={<ApartmentOutlined />}
                  loading={gstFetching}
                  onClick={() => void fetchGstDetails()}
                >
                  Fetch from GST
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="name" label="Party / Legal Name" rules={[{ required: true, message: "Name is required" }]}>
            <Input autoFocus />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="code" label="Code / PAN"><Input /></Form.Item></Col>
            <Col span={12}>
              <Form.Item name="type" label="Type" initialValue="Customer">
                <Select options={[
                  { value: "Customer", label: "Customer" },
                  { value: "Vendor", label: "Vendor" },
                  { value: "Person", label: "Person" },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="phone" label="Phone"><Input /></Form.Item>
        </Form>
      </StaticModal>

      {/* ── Quick Add Branch Modal ──────────────────────────────────── */}
      <StaticModal
        title="Add New Branch"
        open={branchCreateOpen}
        onCancel={() => { setBranchCreateOpen(false); branchCreateForm.resetFields(); }}
        onOk={() => void createBranchInline()}
        okText={branchCreateSaving ? "Saving..." : "Save Branch"}
        okButtonProps={{ loading: branchCreateSaving }}
        afterOpenChange={(vis) => {
          if (vis) {
            branchCreateForm.resetFields();
            if (branchCreatePrefill) branchCreateForm.setFieldValue("branchName", branchCreatePrefill);
          }
        }}
        destroyOnHidden maskClosable={false}
      >
        <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.65 }}>
          <kbd style={{ padding: "1px 5px", border: "1px solid currentColor", borderRadius: 3 }}>Ctrl+S</kbd> Save &nbsp;·&nbsp;
          <kbd style={{ padding: "1px 5px", border: "1px solid currentColor", borderRadius: 3 }}>Alt+W</kbd> Close
        </div>
        <Form form={branchCreateForm} layout="vertical">
          <Form.Item name="branchName" label="Branch Name" rules={[{ required: true, message: "Branch name is required" }]}>
            <Input autoFocus maxLength={100} placeholder="e.g. Mumbai Central Branch" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="branchCode" label="Branch Code">
                <Input maxLength={20} placeholder="e.g. MUM-01" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="city" label="City">
                <Input maxLength={80} placeholder="e.g. Mumbai" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </StaticModal>

      {/* ── Documents Drawer ─────────────────────────────────────────── */}
      <StaticDrawer
        title={`Documents — ${docsConsignment?.consignmentNo ?? ""}`}
        width={600} open={docsOpen}
        onClose={() => { setDocsOpen(false); setDocsConsignment(null); }}
        footer={<div style={{ display: "flex", justifyContent: "flex-end" }}><Button onClick={() => { setDocsOpen(false); setDocsConsignment(null); }}>Close</Button></div>}
        destroyOnHidden maskClosable={false}      >
        <EntityAttachmentsPanel
          entityType="consignment"
          entityId={docsConsignment?.id}
          entityLabel={docsConsignment?.consignmentNo}
        />
      </StaticDrawer>
    </Card>
  );
};
